import {css, view, createView} from '@sail/ui';
import {
  css as deprecatedCss,
  view as deprecatedView,
} from '@sail/ui/deprecated';
import {useTheme} from '../../ThemeProvider';
import {
  useNewUniversalHelpDesign,
  useUniversalHelpDotComDesign,
} from '../../FeatureFlagsProvider';
import {renderMarkdownInline} from '../../helpers/markdown';
import {dotComColors} from '../../theme/dotCom';

// Note: this is only used in the old designs
const backgroundToFontColorMapping = {
  neutral100: 'primary',
  neutral600: deprecatedCss.value('white'),
} as const;

const dotComBackgroundToFontColorMapping = {
  neutral100: deprecatedCss.value(dotComColors.brand600),
  neutral600: deprecatedCss.value(dotComColors.brand),
} as const;

export type Props = {
  body: string;
  background?: 'neutral100' | 'neutral600';
  textSize?: 'default' | 'small';
  css?: any;
};

export const TextBody = createView(
  ({
    body,
    background = 'neutral100',
    textSize = 'default',
    ...props
  }: Props) => {
    const useNewDesign = useNewUniversalHelpDesign();
    const useDotComDesign = useUniversalHelpDotComDesign();
    const theme = useTheme();

    if (useNewDesign) {
      // eslint-disable-next-line no-use-before-define
      return <TextBodyNext body={body} {...props} />;
    }

    return (
      <deprecatedView.div
        css={{
          stack: 'y',
          gap: 'small',
          '& a': {
            color: deprecatedCss.value(theme.linkColor),
          },
        }}
        {...props}
      >
        <deprecatedView.div>
          {body.split(/\r?\n/g).map((p, i) => (
            <deprecatedView.span
              // eslint-disable-next-line react/no-array-index-key
              key={`text-row-body-${i}`}
              css={{
                color: useDotComDesign
                  ? dotComBackgroundToFontColorMapping[background]
                  : backgroundToFontColorMapping[background],
                font: textSize === 'small' ? 'caption' : 'body',
              }}
            >
              <deprecatedView.span
                // Sanitize the html here so that we prevent any security risks
                //
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{
                  __html: renderMarkdownInline(p),
                }}
              />
              <br />
            </deprecatedView.span>
          ))}
        </deprecatedView.div>
      </deprecatedView.div>
    );
  },
);

const TextBodyNext = createView(
  ({body, textSize = 'default', ...props}: Omit<Props, 'background'>) => {
    return (
      <view.div
        className="uh-conversations-chat-text-body"
        css={{
          stack: 'y',
          gap: '0px',
          ...props.css,
        }}
        uses={[
          css.global({
            '.uh-conversations-chat-text-body a': {
              fontWeight: 'semibold',
              textColor: 'action.primary',
            },
            '.uh-conversations-chat-text-body a:hover': {
              textColor: 'action.primary.hovered',
            },
          }),
          textSize === 'small'
            ? css({
                fontSize: 12,
                lineHeight: '1.4em',
              })
            : null,
        ]}
      >
        {body.split(/\r?\n/g).map((p, i) => {
          // Default to the zero-width space unicode character.
          // Otherwise an empty node won't take up a line and will
          // collapse to 0 height
          const rendered =
            renderMarkdownInline(p, {removeTargetInLinks: false}) || '\u200B';
          return (
            <view.span
              css={{
                wordBreak: 'break-word',
              }}
              // eslint-disable-next-line react/no-array-index-key
              key={`text-row-body-${i}`}
              // Sanitize the html here so that we prevent any security risks
              //
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{__html: rendered}}
            />
          );
        })}
      </view.div>
    );
  },
);
